import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { invitationAPI } from '../api';

function Register() {
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isReferral, setIsReferral] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!invitationToken) {
      setError('Registration requires a valid invitation');
      setLoading(false);
      return;
    }

    // Validate invitation token
    const validateInvitation = async () => {
      try {
        const response = await invitationAPI.validate(invitationToken);
        setInvitation(response.data);
        setIsReferral(response.data.invitation?.is_referral || false);

        // Pre-fill email and name when the invite targeted a specific person
        if (response.data.invitation?.is_referral) {
          setFormData(prev => ({
            ...prev,
            email: '',
            name: ''
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            email: response.data.invitation.email,
            name: response.data.invitation.name
          }));
        }

        // If user already exists, redirect to login
        if (response.data.user_exists) {
          navigate(`/login?redirect=/invite/${invitationToken}`);
          return;
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };

    validateInvitation();
  }, [invitationToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      const response = await register(
        formData.email,
        formData.password,
        formData.name,
        invitationToken
      );

      if (!response.group_joined && response.group_join_message) {
        navigate('/groups', { state: { notice: response.group_join_message } });
      } else if (response.group_id) {
        navigate(`/groups/${response.group_id}`);
      } else {
        navigate('/groups');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading && !invitation) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="loading">
            <div className="spinner"></div>
            <p>Validating invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>❌ Invalid Invitation</h2>
          <p className="error-message">{error}</p>
          <p>Please contact the group organizer for a new invitation.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>🎁 Create Your Account</h2>

        {invitation && (
          <div className="info-message">
            {invitation.referral ? (
              <>
                <strong>{invitation.referral.inviter_name}</strong> invited you to WhoIsYourSanta.
              </>
            ) : (
              <>
                <strong>{invitation.group.organizer_name}</strong> invited you to join{' '}
                <strong>{invitation.group.name}</strong>
              </>
            )}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={!isReferral}
              style={
                !isReferral
                  ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' }
                  : undefined
              }
            />
            <small style={{ color: '#666', fontSize: '0.875rem' }}>
              {isReferral
                ? 'Enter the email you want to use for your WhoIsYourSanta account.'
                : 'This email address was invited to the group'}
            </small>
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
              disabled={loading}
              placeholder="At least 6 characters"
            />
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength="6"
              disabled={loading}
              placeholder="Re-enter your password"
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-toggle">
          Already have an account?{' '}
          <button onClick={() => navigate(`/login?redirect=/invite/${invitationToken}`)}>
            Login instead
          </button>
        </div>
      </div>
    </div>
  );
}

export default Register;
